'use client'

import { use, useState, useMemo } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'

// =============================================================================
// DATA
// =============================================================================

const careerStageData: Record<string, {
  name: string
  description: string
  yearsActive: string
  avgPrice: string
  volume: string
  lots: string
  change: number
  characteristics: string[]
}> = {
  'emerging': {
    name: 'Emerging',
    description: 'Artists in the early stages of their careers, typically within 10 years of their first professional exhibition. Often represented by small galleries or self-represented.',
    yearsActive: '<10 years',
    avgPrice: '$28K',
    volume: '$420M',
    lots: '45K',
    change: 32.4,
    characteristics: ['Primary market focus', 'Gallery representation building', 'Limited auction history', 'High volatility'],
  },
  'mid-career': {
    name: 'Mid-Career',
    description: 'Artists with established gallery representation and growing institutional recognition. Typically 10-25 years into their professional careers with consistent exhibition history.',
    yearsActive: '10-25 years',
    avgPrice: '$185K',
    volume: '$1.8B',
    lots: '62K',
    change: 12.8,
    characteristics: ['Secondary market active', 'Institutional acquisitions', 'Major gallery representation', 'Moderate volatility'],
  },
  'established': {
    name: 'Established',
    description: 'Artists with extensive museum exhibitions, major gallery representation, and consistent auction performance over decades. Blue-chip status confirmed.',
    yearsActive: '25+ years',
    avgPrice: '$1.2M',
    volume: '$4.2B',
    lots: '48K',
    change: 6.2,
    characteristics: ['Deep auction history', 'Museum collection presence', 'Global recognition', 'Lower volatility'],
  },
  'historical': {
    name: 'Historical',
    description: 'Deceased artists whose work has stood the test of time. Market driven by estate sales, deaccessions, and secondary market transactions.',
    yearsActive: 'Deceased',
    avgPrice: '$4.8M',
    volume: '$6.8B',
    lots: '25K',
    change: 3.1,
    characteristics: ['Fixed supply', 'Provenance critical', 'Estate-controlled', 'Most stable'],
  },
}

const topArtistsByStage: Record<string, { name: string; slug: string; initials: string; color: string; volume: string; change: number; medium: string }[]> = {
  'emerging': [
    { name: 'Jadé Fadojutimi', slug: 'jade-fadojutimi', initials: 'JF', color: '#6B5B95', volume: '$8.2M', change: 156.2, medium: 'Paintings' },
    { name: 'Lucy Bull', slug: 'lucy-bull', initials: 'LB', color: '#F7CAC9', volume: '$6.4M', change: 78.4, medium: 'Paintings' },
    { name: 'Issy Wood', slug: 'issy-wood', initials: 'IW', color: '#88B04B', volume: '$5.2M', change: 48.5, medium: 'Paintings' },
    { name: 'Anna Weyant', slug: 'anna-weyant', initials: 'AW', color: '#92A8D1', volume: '$4.8M', change: 45.2, medium: 'Paintings' },
    { name: 'Emily Mae Smith', slug: 'emily-mae-smith', initials: 'ES', color: '#F4A460', volume: '$3.6M', change: 32.8, medium: 'Paintings' },
  ],
  'mid-career': [
    { name: 'Amoako Boafo', slug: 'amoako-boafo', initials: 'AB', color: '#4ECDC4', volume: '$45M', change: 42.1, medium: 'Paintings' },
    { name: 'Flora Yukhnovich', slug: 'flora-yukhnovich', initials: 'FY', color: '#E85D75', volume: '$38M', change: 38.2, medium: 'Paintings' },
    { name: 'Christina Quarles', slug: 'christina-quarles', initials: 'CQ', color: '#F7CAC9', volume: '$32M', change: 89.4, medium: 'Paintings' },
    { name: 'Salman Toor', slug: 'salman-toor', initials: 'ST', color: '#5D5D5D', volume: '$28M', change: 22.1, medium: 'Paintings' },
    { name: 'Shara Hughes', slug: 'shara-hughes', initials: 'SH', color: '#9B7EBD', volume: '$24M', change: 52.1, medium: 'Paintings' },
  ],
  'established': [
    { name: 'Yayoi Kusama', slug: 'yayoi-kusama', initials: 'YK', color: '#E85D75', volume: '$180M', change: 18.4, medium: 'Paintings' },
    { name: 'Gerhard Richter', slug: 'gerhard-richter', initials: 'GR', color: '#7B68EE', volume: '$156M', change: -2.3, medium: 'Paintings' },
    { name: 'David Hockney', slug: 'david-hockney', initials: 'DH', color: '#4A90D9', volume: '$142M', change: 6.2, medium: 'Paintings' },
    { name: 'Banksy', slug: 'banksy', initials: 'B', color: '#2A2A2A', volume: '$98M', change: 8.5, medium: 'Prints' },
    { name: 'Yoshitomo Nara', slug: 'yoshitomo-nara', initials: 'YN', color: '#FF69B4', volume: '$72M', change: 24.7, medium: 'Paintings' },
  ],
  'historical': [
    { name: 'Jean-Michel Basquiat', slug: 'jean-michel-basquiat', initials: 'JB', color: '#E63946', volume: '$285M', change: 12.1, medium: 'Paintings' },
    { name: 'Claude Monet', slug: 'claude-monet', initials: 'CM', color: '#9B7EBD', volume: '$320M', change: 4.8, medium: 'Paintings' },
    { name: 'Pablo Picasso', slug: 'pablo-picasso', initials: 'PP', color: '#4A90D9', volume: '$285M', change: 3.8, medium: 'Paintings' },
    { name: 'Andy Warhol', slug: 'andy-warhol', initials: 'AW', color: '#FF4500', volume: '$142M', change: 6.8, medium: 'Prints' },
    { name: 'Mark Rothko', slug: 'mark-rothko', initials: 'MR', color: '#8B0000', volume: '$98M', change: 8.4, medium: 'Paintings' },
  ],
}

const recentSalesByStage: Record<string, { artist: string; artistSlug: string; work: string; house: string; hammer: string; premium: string; date: string }[]> = {
  'emerging': [
    { artist: 'Jadé Fadojutimi', artistSlug: 'jade-fadojutimi', work: 'Yet, To Be Titled', house: 'Phillips', hammer: '$1.5M', premium: '+121%', date: '1 week ago' },
    { artist: 'Lucy Bull', artistSlug: 'lucy-bull', work: 'Untitled', house: "Christie's", hammer: '$890K', premium: '+45%', date: '2 weeks ago' },
    { artist: 'Anna Weyant', artistSlug: 'anna-weyant', work: 'Falling Woman', house: "Sotheby's", hammer: '$680K', premium: '+38%', date: '3 weeks ago' },
  ],
  'mid-career': [
    { artist: 'Amoako Boafo', artistSlug: 'amoako-boafo', work: 'Black Diaspora', house: "Christie's", hammer: '$3.2M', premium: '+28%', date: '5 days ago' },
    { artist: 'Christina Quarles', artistSlug: 'christina-quarles', work: 'Always Felt Like Falling', house: "Sotheby's", hammer: '$2.8M', premium: '+42%', date: '1 week ago' },
    { artist: 'Flora Yukhnovich', artistSlug: 'flora-yukhnovich', work: 'Warm, Wet n Wild', house: 'Phillips', hammer: '$2.4M', premium: '+35%', date: '2 weeks ago' },
  ],
  'established': [
    { artist: 'Yayoi Kusama', artistSlug: 'yayoi-kusama', work: 'Infinity Nets', house: "Sotheby's", hammer: '$8.4M', premium: '+18%', date: '3 days ago' },
    { artist: 'Banksy', artistSlug: 'banksy', work: 'Girl with Balloon', house: "Sotheby's", hammer: '$12.1M', premium: '+15%', date: '1 week ago' },
    { artist: 'David Hockney', artistSlug: 'david-hockney', work: 'Pool with Two Figures', house: "Christie's", hammer: '$18.2M', premium: '+8%', date: '2 weeks ago' },
  ],
  'historical': [
    { artist: 'Jean-Michel Basquiat', artistSlug: 'jean-michel-basquiat', work: 'Untitled (Devil)', house: "Christie's", hammer: '$42.4M', premium: '+6%', date: '5 days ago' },
    { artist: 'Claude Monet', artistSlug: 'claude-monet', work: 'Nymphéas', house: "Christie's", hammer: '$74.0M', premium: '+12%', date: '3 days ago' },
    { artist: 'Andy Warhol', artistSlug: 'andy-warhol', work: 'Shot Sage Blue Marilyn', house: "Christie's", hammer: '$28.5M', premium: '+14%', date: '1 week ago' },
  ],
}

// Risk/return characteristics by stage
const riskReturnData: Record<string, { avgReturn: number; volatility: number; sharpe: number; maxDrawdown: number }> = {
  'emerging': { avgReturn: 32.4, volatility: 48.2, sharpe: 0.67, maxDrawdown: -42.1 },
  'mid-career': { avgReturn: 18.6, volatility: 28.4, sharpe: 0.65, maxDrawdown: -24.8 },
  'established': { avgReturn: 8.2, volatility: 14.6, sharpe: 0.56, maxDrawdown: -12.4 },
  'historical': { avgReturn: 4.8, volatility: 8.2, sharpe: 0.58, maxDrawdown: -8.6 },
}

// =============================================================================
// COMPONENTS
// =============================================================================

function Sparkline({ positive }: { positive: boolean }) {
  const data = positive ? [20, 22, 21, 25, 28, 32, 35, 38, 42] : [40, 38, 35, 37, 32, 28, 25, 22, 20]
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const width = 48
  const height = 20

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={positive ? 'var(--positive)' : 'var(--negative)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function CareerStagePage({ params }: { params: Promise<{ stage: string }> }) {
  const { stage } = use(params)
  const { user } = useAuth()
  const isFreeUser = user?.tier === 'free' || !user

  const data = careerStageData[stage] || careerStageData['emerging']
  const topArtists = topArtistsByStage[stage] || topArtistsByStage['emerging']
  const recentSales = recentSalesByStage[stage] || recentSalesByStage['emerging']
  const riskReturn = riskReturnData[stage] || riskReturnData['emerging']

  // All career stages for navigation
  const allStages = Object.entries(careerStageData).map(([id, d]) => ({ id, name: d.name, change: d.change }))

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-4">
        <Link href="/app/market" className="hover:text-text-secondary transition-colors">Market</Link>
        <span>›</span>
        <span className="text-text-primary">{data.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-2xl md:text-3xl font-light">{data.name} Artists</h1>
            <span className="text-sm text-text-tertiary">{data.yearsActive}</span>
          </div>
          <p className="text-text-secondary text-sm max-w-2xl">{data.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <TierGate feature="export" showPrompt={false}>
            <button className="px-3 py-1.5 rounded-full text-sm bg-accent-primary text-white flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>
          </TierGate>
        </div>
      </div>

      {/* Stage Navigation Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {allStages.map((s) => (
          <Link
            key={s.id}
            href={`/app/market/career/${s.id}`}
            className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-2 ${
              s.id === stage
                ? 'bg-text-primary text-white'
                : 'bg-card border border-border text-text-secondary hover:border-border-hover'
            }`}
          >
            {s.name}
            <span className={`text-xs tabular-nums ${s.change >= 0 ? 'text-positive' : 'text-negative'}`}>
              {s.change >= 0 ? '+' : ''}{s.change}%
            </span>
          </Link>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Volume</p>
          <p className="text-xl font-display font-light">{data.volume}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Lots</p>
          <p className="text-xl font-display font-light">{data.lots}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Avg. Price</p>
          <p className="text-xl font-display font-light">{data.avgPrice}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">YoY</p>
          <p className="text-xl font-display font-light" style={{ color: data.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}>
            {data.change >= 0 ? '+' : ''}{data.change}%
          </p>
        </div>
        {/* AI Signal */}
        <div className="card p-4 border-l-2" style={{ borderLeftColor: 'var(--accent-primary)' }}>
          <p className="text-xs text-text-tertiary mb-1 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            AI Signal
          </p>
          <p className="text-sm font-medium" style={{ color: data.change >= 10 ? 'var(--positive)' : 'var(--text-primary)' }}>
            {data.change >= 20 ? 'Strong momentum' : data.change >= 10 ? 'Growing interest' : data.change >= 0 ? 'Stable' : 'Cooling'}
          </p>
        </div>
      </div>

      {/* Characteristics */}
      <div className="card p-4 mb-6">
        <h3 className="font-medium text-sm mb-3">Stage Characteristics</h3>
        <div className="flex flex-wrap gap-2">
          {data.characteristics.map((char, i) => (
            <span key={i} className="px-3 py-1.5 rounded-full text-xs bg-page border border-border text-text-secondary">
              {char}
            </span>
          ))}
        </div>
      </div>

      {/* Two Column: Artists + Risk/Return */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Top Artists */}
        <section className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Top {data.name} Artists</h2>
            <Link href="/app/artists" className="text-xs text-accent-primary hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {topArtists.map((artist, i) => (
              <Link
                key={artist.slug}
                href={`/app/artists/${artist.slug}`}
                className="flex items-center gap-3 py-2 hover:bg-page -mx-2 px-2 rounded-lg transition-colors"
              >
                <span className="text-xs text-text-tertiary w-4">{i + 1}</span>
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                  style={{ background: artist.color }}
                >
                  {artist.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{artist.name}</p>
                  <p className="text-xs text-text-tertiary">{artist.medium}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Sparkline positive={artist.change >= 0} />
                  <div className="text-right w-20">
                    <p className="text-sm font-medium tabular-nums">{artist.volume}</p>
                    <p className={`text-xs tabular-nums ${artist.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                      {artist.change >= 0 ? '+' : ''}{artist.change}%
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Risk/Return Profile */}
        <section className="card p-5">
          <h2 className="font-medium mb-4">Risk/Return Profile</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm">Avg. Return</span>
                <span className="text-sm font-medium text-positive tabular-nums">+{riskReturn.avgReturn}%</span>
              </div>
              <div className="w-full bg-page rounded-full h-2">
                <div className="h-2 rounded-full bg-positive" style={{ width: `${Math.min(riskReturn.avgReturn * 2, 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm">Volatility</span>
                <span className="text-sm font-medium tabular-nums">{riskReturn.volatility}%</span>
              </div>
              <div className="w-full bg-page rounded-full h-2">
                <div className="h-2 rounded-full bg-accent-warm" style={{ width: `${Math.min(riskReturn.volatility * 1.5, 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm">Max Drawdown</span>
                <span className="text-sm font-medium text-negative tabular-nums">{riskReturn.maxDrawdown}%</span>
              </div>
              <div className="w-full bg-page rounded-full h-2">
                <div className="h-2 rounded-full bg-negative" style={{ width: `${Math.abs(riskReturn.maxDrawdown) * 1.5}%` }} />
              </div>
            </div>
            <div className="pt-3 border-t border-border-subtle">
              <div className="flex items-center justify-between">
                <span className="text-sm">Sharpe Ratio</span>
                <span className="text-sm font-medium tabular-nums">{riskReturn.sharpe.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Recent Sales */}
      <section className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium">Recent {data.name} Sales</h2>
          <Link href="/app/market" className="text-xs text-accent-primary hover:underline">View all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {recentSales.map((sale, i) => (
            <Link
              key={i}
              href={`/app/artists/${sale.artistSlug}`}
              className="p-4 rounded-xl bg-page hover:bg-card transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-tertiary">{sale.house}</span>
                <span className="text-xs text-text-tertiary">{sale.date}</span>
              </div>
              <p className="text-sm font-medium truncate mb-1">{sale.work}</p>
              <p className="text-xs text-text-tertiary mb-2">{sale.artist}</p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-display font-light">{sale.hammer}</span>
                <span className={`text-xs font-medium tabular-nums ${sale.premium.startsWith('+') ? 'text-positive' : 'text-negative'}`}>
                  {sale.premium} vs est.
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Upgrade CTA */}
      {isFreeUser && (
        <section className="card p-5 bg-gradient-to-br from-accent-primary/5 to-transparent border-accent-primary/20">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <h3 className="font-medium mb-1">Access career stage insights</h3>
              <p className="text-sm text-text-secondary">
                AI recommendations for {data.name.toLowerCase()} artists, risk analysis, and portfolio optimization.
              </p>
            </div>
            <Link
              href="/get-started"
              className="px-5 py-2 rounded-xl font-medium text-sm text-white flex-shrink-0"
              style={{ background: 'var(--accent-primary)' }}
            >
              From $199/mo
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}
